import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, MessageCircle, Send, ArrowLeft, Crown, Trash2, Flag, Lock, Copy, VolumeX, Volume2, Eye, RefreshCw } from "lucide-react";
import api from "../lib/api";
import Navbar from "../components/Navbar";

/**
 * Tournament detail — bracket tree visualization + per-bracket chat.
 *
 * Renders all matches grouped by round, with pilot handles + scores +
 * winner highlight. Below the bracket, an in-page chat thread for
 * participants to coordinate / trash-talk.
 */
export default function TournamentDetail() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [me, setMe] = useState(null);
  const [chat, setChat] = useState([]);
  const [mutedUsers, setMutedUsers] = useState([]);
  const [isSpectator, setIsSpectator] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const chatBottomRef = useRef(null);

  const refresh = async () => {
    try {
      const [tr, meR] = await Promise.all([
        api.get(`/tournaments/${id}`),
        api.get("/auth/me"),
      ]);
      setT(tr.data);
      setMe(meR.data);
      // Chat is now also available to spectators once the bracket
      // status is `running`/`complete`. Open brackets stay private.
      try {
        const cr = await api.get(`/tournaments/${id}/chat`);
        setChat(cr.data.messages || []);
        setMutedUsers(cr.data.muted_users || []);
        setIsSpectator(!!cr.data.is_spectator);
      } catch {
        /* swallow — bracket open + non-participant, or canceled */
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("Tournament not found");
      }
    }
  };

  // Ready-Up handler — flips the current pilot's ready flag and
  // re-fetches the bracket. When all pilots are ready, the backend
  // auto-flips status to `running` and the next refresh surfaces the
  // PLAY MATCH callout. Errors surface as a toast (e.g. wrong status,
  // not a participant).
  const [readyingUp, setReadyingUp] = useState(false);
  const readyMe = async () => {
    if (readyingUp) return;
    setReadyingUp(true);
    try {
      await api.post(`/tournaments/${id}/ready`);
      toast.success("Ready up", {
        description: "Bracket starts the moment every pilot is ready.",
        duration: 3000,
      });
      await refresh();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't ready up — try again.",
      );
    } finally {
      setReadyingUp(false);
    }
  };

  useEffect(() => {
    refresh();
    const poll = setInterval(refresh, 8000);
    return () => clearInterval(poll);
  }, [id]);

  // Auto-scroll chat to bottom on new message.
  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat.length]);

  const sendMessage = async (preset = null) => {
    const text = (preset !== null ? preset : draft).trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/tournaments/${id}/chat`, { message: text });
      setChat((c) => [...c, r.data]);
      if (preset === null) setDraft("");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        toast.error("Slow down", {
          description: typeof detail === "string" ? detail : "Max 5 messages per minute.",
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't send message");
      }
    } finally {
      setSending(false);
    }
  };

  // Emoji quick-reactions — 6 preset emojis that one-tap-post into the
  // chat. Reduces typing friction on mobile, where most pilots play.
  const QUICK_REACTIONS = ["🔥", "💀", "😂", "👀", "💯", "🫡"];

  // Delete a chat message (own / host / admin) — soft-delete on the
  // backend; we optimistically drop it from local state so the bubble
  // disappears instantly.
  const deleteMessage = async (msgId) => {
    if (!msgId) return;
    if (!window.confirm("Delete this message? It will disappear for everyone.")) return;
    try {
      await api.delete(`/tournaments/${id}/chat/${msgId}`);
      setChat((c) => c.filter((m) => m.id !== msgId));
      toast.success("Message deleted");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't delete");
    }
  };

  // Report a message — flags it for admin review. After 3 unique
  // reports the backend auto-soft-deletes it, in which case we drop
  // it locally too.
  const reportMessage = async (msgId) => {
    if (!msgId) return;
    try {
      const r = await api.post(`/tournaments/${id}/chat/${msgId}/report`);
      if (r.data.auto_hidden) {
        setChat((c) => c.filter((m) => m.id !== msgId));
        toast.success("Message hidden — 3 pilots flagged it.");
      } else {
        toast.success(`Reported · ${r.data.reports} flag${r.data.reports === 1 ? "" : "s"}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't report");
    }
  };

  // Mute / unmute a participant — host-only. Optimistically updates
  // the local mutedUsers set so the UI reflects the change without a
  // round-trip. The chat poll will reconcile on the next 8s tick.
  const toggleMute = async (userId) => {
    if (!userId) return;
    const isMuted = mutedUsers.includes(userId);
    try {
      if (isMuted) {
        await api.delete(`/tournaments/${id}/mute/${userId}`);
        setMutedUsers((m) => m.filter((u) => u !== userId));
        toast.success("Pilot un-muted");
      } else {
        await api.post(`/tournaments/${id}/mute/${userId}`);
        setMutedUsers((m) => [...m, userId]);
        toast.success("Pilot muted in this bracket");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update mute");
    }
  };

  // Copy join code to clipboard (host-only). Falls back to a manual
  // toast if the clipboard API isn't available (e.g. http preview).
  const copyJoinCode = async (code) => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Code copied · ${code}`);
    } catch {
      toast.success(`Join code: ${code}`);
    }
  };

  if (!t) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" />
        <Navbar />
        <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-16">
          <div className="text-white/50 font-mono-cosmic text-xs tracking-[0.25em] uppercase">
            Loading…
          </div>
        </main>
      </div>
    );
  }

  // Group matches by round for column rendering.
  const rounds = {};
  for (const m of t.matches || []) {
    if (!rounds[m.round]) rounds[m.round] = [];
    rounds[m.round].push(m);
  }
  const roundKeys = Object.keys(rounds).map(Number).sort((a, b) => a - b);
  const isParticipant =
    me && (t.participants || []).some((p) => p.user_id === me.id);
  const myMatch = t.my_match;

  // Helper: lookup a participant by uid.
  const lookup = (uid) => {
    if (!uid) return null;
    return (t.participants || []).find((p) => p.user_id === uid) || null;
  };

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main
        className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-16 fade-in"
        data-testid="tournament-detail-page"
      >
        <Link
          to="/tournaments"
          className="inline-flex items-center gap-1.5 text-white/55 hover:text-white font-mono-cosmic text-[10px] tracking-[0.3em] uppercase mb-4"
        >
          <ArrowLeft className="w-3 h-3" /> Brackets
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="font-mono-cosmic text-[10px] tracking-[0.4em] text-[#ffd700]/70 uppercase mb-2">
            // BRACKET · {t.id}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white">
            Level {t.level} · {t.size}-pilot
          </h1>
          <div className="flex flex-wrap gap-3 mt-2 font-mono-cosmic text-[10px] tracking-[0.2em] uppercase text-white/55">
            <span>{t.buyin} ☾ buy-in</span>
            <span>·</span>
            <span>Pot {t.pot} ☾</span>
            <span>·</span>
            <span>Status: {t.status}</span>
            {!isParticipant && t.status !== "complete" && (
              <>
                <span>·</span>
                <span
                  className="text-[#3aa8ff] flex items-center gap-1"
                  data-testid="tournament-spectating-badge"
                >
                  ◉ SPECTATING
                </span>
              </>
            )}
            {t.winner_id && (
              <>
                <span>·</span>
                <span className="text-[#ffd700] flex items-center gap-1">
                  <Crown className="w-3 h-3" /> {lookup(t.winner_id)?.name || "Unknown"} won
                </span>
              </>
            )}
          </div>
        </div>

        {/* Ready-Up gate — shown when the bracket has filled but
            hasn't started yet. Pilot clicks READY UP; bracket flips
            to `running` only once every participant is ready (bots
            auto-ready at seed time). Countdown shows seconds left
            before the cron force-starts and forfeits un-readied
            pilots into R1 at score 0. */}
        {t.status === "ready-up" && isParticipant && (() => {
          const meParticipant = (t.participants || []).find(
            (p) => p.user_id === me?.id,
          );
          const meReady = !!meParticipant?.ready;
          return (
            <ReadyUpPanel
              t={t}
              meReady={meReady}
              onReady={readyMe}
              busy={readyingUp}
            />
          );
        })()}

        {/* My match callout */}
        {myMatch && t.status !== "complete" && t.status !== "ready-up" && (
          <div
            className="mb-6 border border-[#3aa8ff]/60 bg-[#3aa8ff]/10 p-4 flex items-center justify-between flex-wrap gap-3"
            data-testid="tournament-my-match"
          >
            <div>
              <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#3aa8ff]">
                YOUR MATCH · ROUND {myMatch.round}
              </div>
              <div className="text-sm text-white mt-1">
                You vs{" "}
                <span className="text-white/85">
                  {lookup(myMatch.pa === me?.id ? myMatch.pb : myMatch.pa)?.name || "TBD"}
                </span>
              </div>
              <MatchDeadlineTimer deadline={myMatch.deadline} />
            </div>
            <Link
              to={`/play/${t.level}?tournament_id=${t.id}`}
              className="px-4 py-2 border border-[#3aa8ff] text-[#3aa8ff] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase hover:bg-[#3aa8ff]/15"
              data-testid="tournament-my-match-play-btn"
            >
              PLAY MATCH
            </Link>
          </div>
        )}

        {/* Payouts summary — shown only after the bracket completes.
            Each pilot's net moonstone P&L is displayed alongside the
            human/bot indicator so players can quickly see the outcome. */}
        {t.status === "complete" && Array.isArray(t.payouts) && t.payouts.length > 0 && (
          <PayoutsSummary
            payouts={t.payouts}
            houseCut={t.house_cut}
            finalPrize={t.final_prize}
            humanBonus={t.human_bonus || 0}
            humansBeaten={t.humans_beaten || 0}
            pot={t.pot}
            meId={me?.id}
            tournamentLevel={t.level}
          />
        )}

        {/* Shared Replay Seed — public to anyone viewing the bracket.
            Lets pilots copy a replay link so friends can challenge
            themselves on the EXACT same RNG conditions outside the
            live bracket. The link encodes the bracket_seed + level
            so Play.jsx can surface a REPLAY badge and propagate the
            seed into the in-canvas RNG. */}
        {t.bracket_seed && (
          <SharedSeedPanel
            seed={t.bracket_seed}
            level={t.level}
            bracketId={t.id}
          />
        )}

        {/* Bracket tree — horizontal column-per-round layout */}
        <div className="mb-8">
          <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">
            // BRACKET TREE
          </div>
          <div
            className="grid gap-4 overflow-x-auto pb-2"
            style={{ gridTemplateColumns: `repeat(${roundKeys.length}, minmax(180px, 1fr))` }}
            data-testid="tournament-bracket-tree"
          >
            {roundKeys.map((r) => {
              const isFinal = r === Math.max(...roundKeys);
              return (
                <div key={r} className="space-y-2" data-testid={`tournament-round-${r}`}>
                  <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/45">
                    {isFinal ? "FINALS" : `ROUND ${r}`}
                  </div>
                  {rounds[r].map((m) => {
                    const pa = lookup(m.pa);
                    const pb = lookup(m.pb);
                    const winner = m.winner;
                    return (
                      <div
                        key={`${r}-${m.slot}`}
                        className={`border bg-black/35 ${
                          winner ? "border-white/15" : "border-[#ffd700]/30"
                        }`}
                        data-testid={`tournament-match-${r}-${m.slot}`}
                      >
                        <PilotRow
                          pilot={pa}
                          score={m.score_a}
                          winner={winner === m.pa}
                          loser={winner && winner !== m.pa}
                        />
                        <div className="border-t border-white/10" />
                        <PilotRow
                          pilot={pb}
                          score={m.score_b}
                          winner={winner === m.pb}
                          loser={winner && winner !== m.pb}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* Participants list */}
        <div className="mb-8">
          <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">
            // PARTICIPANTS ({t.participants.length}/{t.size})
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="tournament-participants">
            {t.participants.map((p) => (
              <span
                key={p.user_id}
                className={`px-2 py-1 border font-mono-cosmic text-[10px] tracking-[0.15em] uppercase flex items-center gap-1 ${
                  p.eliminated
                    ? "border-white/10 text-white/30 line-through"
                    : p.is_bot
                      ? "border-[#b026ff]/40 text-[#b026ff]/85"
                      : "border-white/20 text-white/85"
                }`}
                data-testid={p.is_bot ? `tournament-bot-${p.user_id}` : undefined}
              >
                {p.is_bot && (
                  <span
                    className="px-1 border border-[#b026ff]/60 text-[#b026ff] text-[8px]"
                    title="AI opponent — auto-fills empty bracket seats"
                  >
                    AI
                  </span>
                )}
                <span>{p.name}{p.tag ? `#${p.tag}` : ""}</span>
                {p.is_bot && t.bot_career && t.bot_career[p.user_id] && (() => {
                  const c = t.bot_career[p.user_id];
                  const total = c.matches_total || 0;
                  const rate = total > 0 ? Math.round((c.win_rate || 0) * 100) : 0;
                  const r = c.rivalry || { wins: 0, losses: 0, total: 0 };
                  // Tone the rivalry chip by your record: green when up,
                  // pink when down, neutral when even or no plays.
                  let rivalryAccent = "text-white/55";
                  if (r.total > 0) {
                    if (r.wins > r.losses) rivalryAccent = "text-[#28e070]";
                    else if (r.losses > r.wins) rivalryAccent = "text-[#ff5577]";
                  }
                  return (
                    <>
                      <span
                        className="ml-1 px-1 border border-[#b026ff]/30 text-[#b026ff]/70 text-[9px] tabular-nums"
                        title={total > 0
                          ? `Career: ${c.wins}W–${c.losses}L (${rate}% win rate)`
                          : "Career: no matches yet"}
                        data-testid={`tournament-bot-career-${p.user_id}`}
                      >
                        {c.wins}-{c.losses}
                      </span>
                      {r.total > 0 && (
                        <span
                          className={`ml-0.5 px-1 border border-current/30 ${rivalryAccent} text-[9px] tabular-nums`}
                          title={`Your head-to-head vs ${p.name}: ${r.wins}W–${r.losses}L`}
                          data-testid={`tournament-bot-rivalry-${p.user_id}`}
                        >
                          You {r.wins}-{r.losses}
                        </span>
                      )}
                    </>
                  );
                })()}
                {p.user_id === t.winner_id && (
                  <span className="text-[#ffd700]">★</span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* Chat thread (participants only) */}
        {/* Bracket chat — visible to participants always, and to
            spectators once the bracket is `running`/`complete`. The
            input is hidden in spectator mode (read-only mirror) and
            in muted mode (host silenced this pilot). */}
        {(isParticipant || (!isParticipant && (t.status === "running" || t.status === "complete"))) && (
          <div
            className="border border-white/10 bg-black/30 p-4"
            data-testid="tournament-chat"
          >
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle className="w-4 h-4 text-[#b026ff]" />
              <div className="font-heading uppercase tracking-[0.25em] text-sm text-white">
                Bracket Chat
              </div>
              <span
                className="ml-auto font-mono-cosmic text-[9px] tracking-[0.3em] uppercase flex items-center gap-1.5"
                data-testid="tournament-chat-mode"
              >
                {isSpectator ? (
                  <>
                    <Eye className="w-3 h-3 text-[#3aa8ff]" />
                    <span className="text-[#3aa8ff]">spectator · read-only</span>
                  </>
                ) : (
                  <span className="text-white/40">participants only</span>
                )}
              </span>
            </div>
            {/* Host-only join-code chip on invite-only brackets — copy
                to share with squad. Suppressed on running/complete
                because the bracket is full and the code can no longer
                gate joins. */}
            {t.invite_only && t.created_by === me?.id && t.join_code && t.status === "open" && (
              <div
                className="mb-3 flex items-center gap-2 px-2 py-1.5 border border-[#ffd700]/40 bg-[#ffd700]/5"
                data-testid="tournament-host-join-code"
              >
                <Lock className="w-3 h-3 text-[#ffd700]" />
                <span className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/60">
                  Invite code
                </span>
                <span className="font-mono-cosmic text-xs tracking-[0.25em] text-[#ffd700] tabular-nums">
                  {t.join_code}
                </span>
                <button
                  type="button"
                  onClick={() => copyJoinCode(t.join_code)}
                  className="ml-auto text-white/55 hover:text-[#ffd700] transition"
                  title="Copy join code"
                  data-testid="tournament-host-join-code-copy"
                >
                  <Copy className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm("Rotate the invite code? Old code stops working immediately.")) return;
                    try {
                      const r = await api.post(`/tournaments/${id}/regenerate-code`);
                      toast.success(`New code · ${r.data.join_code}`);
                      refresh();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Couldn't rotate code");
                    }
                  }}
                  className="text-white/55 hover:text-[#ff007f] transition"
                  title="Rotate code (old one stops working)"
                  data-testid="tournament-host-join-code-rotate"
                >
                  <RefreshCw className="w-3 h-3" />
                </button>
              </div>
            )}
            <div className="max-h-72 overflow-y-auto space-y-2 pr-1 mb-3">
              {chat.length === 0 && (
                <div className="text-white/35 font-mono-cosmic text-[10px] tracking-[0.2em] uppercase text-center py-6">
                  {isSpectator ? "// silent so far" : "Be the first to talk smack —"}
                </div>
              )}
              {chat.map((msg, idx) => {
                const isMe = msg.user_id === me?.id;
                const isHost = me?.id && t?.created_by === me.id;
                const isAdmin = me?.role === "admin";
                const canDelete = !isSpectator && !!msg.id && (isMe || isHost || isAdmin);
                const canReport = !isSpectator && !!msg.id && !isMe;
                const canMute = isHost && !isMe && !!msg.user_id;
                const muted = mutedUsers.includes(msg.user_id);
                const isBot = !!msg.is_bot;
                return (
                  <div
                    key={msg.id || idx}
                    className={`group flex flex-col ${isMe ? "items-end" : "items-start"}`}
                    data-testid={`tournament-chat-msg-${idx}`}
                  >
                    <div
                      className={`max-w-[85%] px-3 py-1.5 ${
                        isMe
                          ? "bg-[#3aa8ff]/15 border border-[#3aa8ff]/40"
                          : isBot
                            ? "bg-[#b026ff]/8 border border-[#b026ff]/35"
                            : "bg-white/5 border border-white/10"
                      }`}
                      data-bot={isBot ? "true" : undefined}
                    >
                      <div className="font-mono-cosmic text-[9px] tracking-[0.2em] uppercase text-white/50 mb-0.5 flex items-center gap-2">
                        <span className={isBot ? "text-[#b026ff]" : ""}>
                          {msg.name}{msg.tag ? `#${msg.tag}` : ""}
                        </span>
                        {isBot && (
                          <span
                            className="px-1 border border-[#b026ff]/55 text-[#b026ff] tracking-[0.2em]"
                            title="AI opponent · personality response"
                            data-testid={`tournament-chat-bot-badge-${idx}`}
                          >
                            AI
                          </span>
                        )}
                        {muted && (
                          <span
                            className="px-1 border border-[#ff4d6d]/50 text-[#ff4d6d] tracking-[0.2em]"
                            title="Muted by host"
                            data-testid={`tournament-chat-muted-badge-${idx}`}
                          >
                            MUTED
                          </span>
                        )}
                        <span className="ml-auto flex gap-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                          {canReport && !isBot && (
                            <button
                              type="button"
                              onClick={() => reportMessage(msg.id)}
                              className="text-white/40 hover:text-[#ffb020]"
                              title="Report message"
                              data-testid={`tournament-chat-report-${idx}`}
                            >
                              <Flag className="w-3 h-3" />
                            </button>
                          )}
                          {canMute && !isBot && (
                            <button
                              type="button"
                              onClick={() => toggleMute(msg.user_id)}
                              className={`transition ${muted ? "text-[#ff4d6d] hover:text-white" : "text-white/40 hover:text-[#ff4d6d]"}`}
                              title={muted ? "Un-mute pilot" : "Mute pilot in this bracket"}
                              data-testid={`tournament-chat-mute-${idx}`}
                            >
                              {muted ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
                            </button>
                          )}
                          {canDelete && !isBot && (
                            <button
                              type="button"
                              onClick={() => deleteMessage(msg.id)}
                              className="text-white/40 hover:text-[#ff4d6d]"
                              title={isMe ? "Delete your message" : "Delete (host)"}
                              data-testid={`tournament-chat-delete-${idx}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      </div>
                      <div className="text-sm text-white break-words">{msg.message}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={chatBottomRef} />
            </div>
            {/* Composer + quick reactions only for participants who
                aren't muted. Spectators + muted pilots see nothing
                below the message list. */}
            {isParticipant && !mutedUsers.includes(me?.id) && (
              <>
                <div
                  className="flex gap-1 mb-2 flex-wrap"
                  data-testid="tournament-chat-reactions"
                >
                  {QUICK_REACTIONS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => sendMessage(emoji)}
                      disabled={sending}
                      className="w-8 h-8 border border-white/15 hover:border-[#3aa8ff] hover:bg-[#3aa8ff]/10 transition flex items-center justify-center text-base disabled:opacity-40"
                      data-testid={`tournament-chat-reaction-${emoji}`}
                      title={`Send ${emoji}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendMessage();
                  }}
                  className="flex gap-2"
                >
                  <input
                    type="text"
                    maxLength={240}
                    placeholder="Send to bracket…"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className="flex-1 bg-black/40 border border-white/15 focus:border-[#3aa8ff] px-3 py-2 text-white font-mono-cosmic text-xs outline-none"
                    data-testid="tournament-chat-input"
                  />
                  <button
                    type="submit"
                    disabled={sending || !draft.trim()}
                    className="px-3 py-2 border border-[#3aa8ff] text-[#3aa8ff] hover:bg-[#3aa8ff]/15 disabled:opacity-40 transition flex items-center gap-1.5"
                    data-testid="tournament-chat-send-btn"
                  >
                    <Send className="w-3.5 h-3.5" />
                    <span className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase">Send</span>
                  </button>
                </form>
              </>
            )}
            {isParticipant && mutedUsers.includes(me?.id) && (
              <div
                className="px-2 py-2 border border-[#ff4d6d]/40 bg-[#ff4d6d]/5 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-[#ff4d6d] text-center"
                data-testid="tournament-chat-self-muted"
              >
                // You've been muted in this bracket
              </div>
            )}
          </div>
        )}

        {!isParticipant && t.status === "open" && (
          <SpectatorJoinCTA
            tournament={t}
            onJoined={() => {
              toast.success("Joined the bracket");
              refresh();
            }}
          />
        )}
        {!isParticipant && t.status === "running" && (
          <div className="border border-[#3aa8ff]/30 bg-black/20 p-4 text-center" data-testid="tournament-spectator-running">
            <div className="text-white/60 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase">
              ◉ Watching live · scores update every 8 seconds
            </div>
          </div>
        )}
      </main>
    </div>
  );
}


/**
 * Spectator-side join CTA. Renders the standard "JOIN BRACKET" button
 * for public brackets, but for invite-only brackets it shows a join
 * code input inline so a pilot landing via direct link can paste the
 * code and join in one tap (no separate page).
 */
function SpectatorJoinCTA({ tournament: t, onJoined }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const spotsLeft = t.size - t.participants.length;
  const join = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/tournaments/${t.id}/join`,
        t.invite_only ? { join_code: code.trim() } : {},
      );
      onJoined?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't join");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="border border-[#ffd700]/30 bg-black/20 p-6 text-center"
      data-testid="tournament-spectator-cta"
    >
      <Trophy className="w-6 h-6 mx-auto text-[#ffd700]/60 mb-2" />
      <div className="text-white/65 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase mb-3 flex items-center justify-center gap-2">
        {t.invite_only && (
          <span className="text-[#ffd700] flex items-center gap-1">
            <Lock className="w-3 h-3" /> PRIVATE
          </span>
        )}
        <span>
          {spotsLeft} spot{spotsLeft === 1 ? "" : "s"} left · Spectating live
        </span>
      </div>
      {t.invite_only ? (
        <div className="flex flex-col sm:flex-row items-stretch gap-2 max-w-sm mx-auto">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="JOIN CODE"
            maxLength={16}
            className="flex-1 bg-black/50 border border-[#ffd700]/40 focus:border-[#ffd700] px-3 py-2 text-[#ffd700] font-mono-cosmic text-xs tracking-[0.3em] uppercase outline-none text-center tabular-nums"
            data-testid="tournament-invite-code-input"
          />
          <button
            type="button"
            onClick={join}
            disabled={busy || !code.trim()}
            className="px-4 py-2 border border-[#ffd700] text-[#ffd700] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase hover:bg-[#ffd700]/10 disabled:opacity-40 transition"
            data-testid="tournament-invite-join-btn"
          >
            {busy ? "JOINING…" : `JOIN · ${t.buyin} ☾`}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={join}
          disabled={busy}
          className="inline-block px-3 py-1.5 border border-[#ffd700] text-[#ffd700] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase hover:bg-[#ffd700]/10 disabled:opacity-40 transition"
          data-testid="tournament-public-join-btn"
        >
          {busy ? "JOINING…" : `JOIN BRACKET (${t.buyin} ☾)`}
        </button>
      )}
    </div>
  );
}

function PilotRow({ pilot, score, winner, loser }) {
  return (
    <div
      className={`flex items-center justify-between px-3 py-2 ${
        winner ? "bg-[#ffd700]/10" : loser ? "opacity-50" : ""
      }`}
    >
      <div className="font-mono-cosmic text-[10px] tracking-[0.15em] uppercase truncate">
        {pilot ? (
          <span className={winner ? "text-[#ffd700]" : "text-white/85"}>
            {pilot.name}{pilot.tag ? `#${pilot.tag}` : ""}
          </span>
        ) : (
          <span className="text-white/30">— TBD —</span>
        )}
      </div>
      <div className="font-mono-cosmic text-[11px] tabular-nums text-white/65 ml-2">
        {score === null || score === undefined ? "—" : score.toLocaleString()}
      </div>
    </div>
  );
}

/** Live countdown for the pilot's active match deadline. Auto-refreshes
 * every second. Renders an orange-pulsing warning under 60s + red after
 * 30s so pilots feel the urgency. Hidden when there's no deadline (e.g.
 * waiting for opponent to advance) or when the deadline has passed. */
function MatchDeadlineTimer({ deadline }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!deadline) return null;
  const remaining = Math.max(0, new Date(deadline).getTime() - now);
  if (remaining <= 0) {
    return (
      <div
        className="font-mono-cosmic text-[10px] tracking-[0.2em] uppercase text-[#ff5577] mt-1"
        data-testid="tournament-match-deadline-expired"
      >
        ⏰ Deadline lapsed · sweep imminent
      </div>
    );
  }
  const secs = Math.floor(remaining / 1000);
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  const urgent = secs <= 60;
  const critical = secs <= 30;
  const color = critical ? "#ff5577" : urgent ? "#ffae00" : "#28e070";
  const totalMs = 5 * 60 * 1000;
  const pct = Math.min(100, Math.max(0, (remaining / totalMs) * 100));
  return (
    <div className="mt-1.5" data-testid="tournament-match-deadline">
      <div
        className="font-mono-cosmic text-[11px] tabular-nums uppercase tracking-[0.15em]"
        style={{ color }}
      >
        ⏱ {String(mm).padStart(2, "0")}:{String(ss).padStart(2, "0")} to submit
        <span className="text-white/40 text-[9px] ml-1.5 tracking-normal normal-case">
          (auto-forfeit if you miss)
        </span>
      </div>
      <div className="h-0.5 bg-white/10 mt-1 max-w-[280px]">
        <div
          className={`h-full transition-[width] duration-1000 ${critical ? "animate-pulse" : ""}`}
          style={{ width: `${pct}%`, background: color, boxShadow: `0 0 6px ${color}aa` }}
        />
      </div>
    </div>
  );
}

/** Bracket payouts summary — shows each pilot's net P&L after the
 * bracket completes. Highlights the viewer's row so they immediately
 * see their personal outcome. House cut is surfaced as a footer line
 * since the platform takes a small fee. */
function PayoutsSummary({ payouts, houseCut, finalPrize, humanBonus = 0, humansBeaten = 0, pot, meId, tournamentLevel }) {
  // Sort: winner first, then humans desc by net, then bots.
  const sorted = [...payouts].sort((a, b) => {
    if (a.is_winner !== b.is_winner) return a.is_winner ? -1 : 1;
    if (a.is_bot !== b.is_bot) return a.is_bot ? 1 : -1;
    return b.net - a.net;
  });
  const myRow = sorted.find((p) => p.user_id === meId);
  const myNet = myRow?.net ?? 0;
  return (
    <section className="mb-8" data-testid="tournament-payouts">
      <div className="flex items-baseline justify-between mb-3 border-b border-white/10 pb-2">
        <div>
          <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#ffd700]/80">
            // FINAL PAYOUT
          </div>
          <h2 className="font-heading text-xl text-white tracking-tight">
            Tournament Settled · Level {tournamentLevel}
          </h2>
        </div>
        {myRow && (
          <div
            className="text-right"
            data-testid="tournament-payouts-my-result"
          >
            <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/45">
              Your result
            </div>
            <div
              className="font-heading text-2xl tabular-nums"
              style={{
                color: myNet > 0 ? "#28e070" : myNet < 0 ? "#ff5577" : "#fff",
              }}
            >
              {myNet > 0 ? "+" : ""}
              {myNet} ☾
            </div>
          </div>
        )}
      </div>
      <div className="border border-white/10 bg-black/30">
        {sorted.map((p) => {
          const isMe = p.user_id === meId;
          const positive = p.net > 0;
          const negative = p.net < 0;
          return (
            <div
              key={p.user_id}
              className={`grid grid-cols-[1fr_auto] gap-4 px-3 py-2 border-b border-white/5 last:border-b-0 items-center ${isMe ? "bg-[#3aa8ff]/8" : ""}`}
              data-testid={`tournament-payout-row-${p.user_id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {p.is_winner && <span className="text-[#ffd700]">★</span>}
                <span
                  className={`font-mono-cosmic text-[11px] tracking-[0.1em] uppercase truncate ${isMe ? "text-[#3aa8ff]" : "text-white/80"}`}
                >
                  {isMe ? "YOU" : p.name}
                  {p.tag && (
                    <span className="text-white/35">#{p.tag}</span>
                  )}
                </span>
                {p.is_bot && (
                  <span className="px-1 border border-[#b026ff]/55 text-[#b026ff] text-[8px] tracking-[0.2em]">
                    AI
                  </span>
                )}
              </div>
              <div className="text-right">
                <div
                  className="font-mono-cosmic text-sm tabular-nums"
                  style={{
                    color: positive ? "#28e070" : negative ? "#ff5577" : "#aaa",
                  }}
                >
                  {positive ? "+" : ""}
                  {p.net} ☾
                </div>
                {p.is_winner && (
                  <div className="font-mono-cosmic text-[9px] tabular-nums text-white/35">
                    won {p.prize} ☾ pot
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="font-mono-cosmic text-[9px] tracking-[0.15em] uppercase text-white/35 mt-2 flex flex-wrap gap-x-3 gap-y-1">
        <span>Pot: <span className="text-white/55 tabular-nums">{pot} ☾</span></span>
        <span>·</span>
        <span>Prize: <span className="text-white/55 tabular-nums">{finalPrize} ☾</span></span>
        <span>·</span>
        <span>House cut: <span className="text-white/55 tabular-nums">{houseCut} ☾</span></span>
        {humanBonus > 0 && (
          <>
            <span>·</span>
            <span className="text-[#ff007f]">
              Human bonus:{" "}
              <span className="tabular-nums">+{humanBonus} ☾</span>
              <span className="text-white/30 ml-1">
                ({humansBeaten} kill{humansBeaten === 1 ? "" : "s"})
              </span>
            </span>
          </>
        )}
      </div>
    </section>
  );
}



/**
 * Ready-Up panel — shown only when the bracket is in `ready-up`
 * status and the viewer is a participant. Shows the current
 * ready-count (e.g. "2/4 ready"), a live seconds-left countdown
 * derived from `ready_at + ready_window_seconds`, and a giant
 * "READY UP" CTA. When all pilots are ready, the backend auto-
 * flips to `running` on the next refresh.
 */
function ReadyUpPanel({ t, meReady, onReady, busy }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  // Compute remaining seconds. `ready_at` is ISO; difference with
  // now + ready_window_seconds yields the countdown.
  const readyAtMs = t?.ready_at ? Date.parse(t.ready_at) : null;
  const totalMs = (t?.ready_window_seconds || 60) * 1000;
  const elapsedMs = readyAtMs ? now - readyAtMs : 0;
  const remainingMs = Math.max(0, totalMs - elapsedMs);
  const remainingSec = Math.ceil(remainingMs / 1000);
  const pctElapsed = Math.min(100, (elapsedMs / totalMs) * 100);
  const readyCount = t?.ready_count ?? 0;
  const total = t?.participants?.length ?? 0;
  const allReady = !!t?.all_ready;
  return (
    <div
      className="mb-6 border border-[#ffd700]/60 bg-[#ffd700]/10 p-4"
      data-testid="tournament-ready-up-panel"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
        <div>
          <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#ffd700]">
            // READY UP · BRACKET FULL
          </div>
          <div className="text-sm text-white mt-1">
            Every pilot must ready up before matches begin.
          </div>
        </div>
        <div className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/70 tabular-nums">
          <span
            className={readyCount === total ? "text-[#28e070]" : "text-[#ffd700]"}
            data-testid="tournament-ready-count"
          >
            {readyCount} / {total} READY
          </span>
          {" · "}
          <span
            className={remainingSec <= 10 ? "text-[#ff5577]" : "text-white/70"}
            data-testid="tournament-ready-countdown"
          >
            {remainingSec}s
          </span>
        </div>
      </div>
      {/* Progress bar — fills as the timeout approaches. Goes red
          in the last 10 seconds. */}
      <div className="h-1 bg-white/10 mb-3 overflow-hidden">
        <div
          className="h-full transition-all"
          style={{
            width: `${pctElapsed}%`,
            background: remainingSec <= 10 ? "#ff5577" : "#ffd700",
          }}
        />
      </div>
      {/* Per-pilot ready indicators */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {(t?.participants || []).map((p) => (
          <div
            key={p.user_id}
            className={`px-2 py-1 border font-mono-cosmic text-[9px] tracking-[0.2em] uppercase ${
              p.ready
                ? "border-[#28e070]/55 text-[#28e070] bg-[#28e070]/10"
                : "border-white/20 text-white/55"
            }`}
            data-testid={`tournament-ready-participant-${p.user_id}`}
          >
            {p.ready ? "✓" : "○"} {p.name || "TBD"}
            {(p.is_bot || p.is_ai) && (
              <span className="ml-1 text-white/40">[BOT]</span>
            )}
          </div>
        ))}
      </div>
      {allReady ? (
        <div
          className="w-full py-2 border border-[#28e070]/55 bg-[#28e070]/10 text-[#28e070] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-center"
          data-testid="tournament-ready-all-ready"
        >
          Starting matches…
        </div>
      ) : meReady ? (
        <div
          className="w-full py-2 border border-[#28e070]/55 bg-[#28e070]/10 text-[#28e070] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-center"
          data-testid="tournament-ready-me-ready"
        >
          You're ready · waiting on {total - readyCount} more
        </div>
      ) : (
        <button
          type="button"
          onClick={onReady}
          disabled={busy}
          className="w-full py-3 border border-[#ffd700] bg-[#ffd700]/20 hover:bg-[#ffd700]/30 text-[#ffd700] font-mono-cosmic text-xs tracking-[0.3em] uppercase disabled:opacity-50"
          data-testid="tournament-ready-up-btn"
        >
          {busy ? "READYING UP…" : "READY UP"}
        </button>
      )}
    </div>
  );
}

// Shared Replay Seed — exposes the bracket's deterministic seed
// (already part of the API payload) and offers a one-tap replay link
// so pilots can challenge friends on identical RNG conditions outside
// the live bracket. The link encodes ?replay_seed=<seed> on /play/<lvl>
// and Play.jsx renders a "REPLAY" badge + propagates the seed into the
// canvas drop/sparkle RNG (best-effort visual parity).
function SharedSeedPanel({ seed, level, bracketId }) {
  const short = seed ? `${seed.slice(0, 6)}…${seed.slice(-4)}` : "—";
  const replayUrl = `${window.location.origin}/play/${level}?replay_seed=${seed}`;
  // OG-rich share URL — hits the FastAPI /api/share/replay/<id> wrapper
  // which emits Open Graph + Twitter Card meta tags and redirects human
  // browsers to /replay/<id>. This is the URL to put in Discord/Twitter
  // for a beautifully unfurled card preview.
  const spectatorUrl = `${window.location.origin}/api/share/replay/${bracketId}`;
  const copySeed = async () => {
    try {
      await navigator.clipboard.writeText(seed);
      toast.success("Seed copied", { description: short });
    } catch {
      toast.success(`Seed: ${seed}`);
    }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(replayUrl);
      toast.success("Replay link copied", {
        description: "Drop it in chat — friends launch the same RNG outside the bracket.",
      });
    } catch {
      toast.success("Replay link", { description: replayUrl });
    }
  };
  const copySpectator = async () => {
    try {
      await navigator.clipboard.writeText(spectatorUrl);
      toast.success("Spectator URL copied", {
        description: "Anyone can view this bracket's scores — no login required.",
      });
    } catch {
      toast.success("Spectator URL", { description: spectatorUrl });
    }
  };
  return (
    <div
      className="mb-6 border border-[#b026ff]/40 bg-[#b026ff]/5 p-4 flex flex-wrap items-center gap-3"
      data-testid="tournament-shared-seed-panel"
      data-bracket-id={bracketId}
    >
      <div className="flex items-center gap-2 min-w-0">
        <RefreshCw className="w-3.5 h-3.5 text-[#b026ff]" />
        <div className="min-w-0">
          <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#b026ff]/85">
            Shared Replay Seed
          </div>
          <div className="font-mono-cosmic text-xs tracking-[0.15em] text-white/85 tabular-nums truncate" data-testid="tournament-seed-value">
            {short}
          </div>
        </div>
      </div>
      <div className="text-white/55 font-mono-cosmic text-[10px] tracking-[0.2em] uppercase max-w-md">
        Send the link to a friend — they'll play Lvl {level} on the exact bracket seed.
      </div>
      <div className="ml-auto flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={copySeed}
          className="px-3 py-1.5 border border-white/15 hover:border-[#b026ff] hover:text-[#b026ff] text-white/65 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition inline-flex items-center gap-1.5"
          data-testid="tournament-copy-seed-btn"
          title={seed}
        >
          <Copy className="w-3 h-3" /> Seed
        </button>
        <button
          type="button"
          onClick={copyLink}
          className="px-3 py-1.5 border border-white/15 hover:border-[#b026ff] hover:text-[#b026ff] text-white/65 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition inline-flex items-center gap-1.5"
          data-testid="tournament-copy-replay-link-btn"
        >
          <Copy className="w-3 h-3" /> Replay Link
        </button>
        <button
          type="button"
          onClick={copySpectator}
          className="px-3 py-1.5 border border-[#b026ff] text-[#b026ff] hover:bg-[#b026ff]/15 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition inline-flex items-center gap-1.5"
          data-testid="tournament-copy-spectator-url-btn"
          title="Public spectator URL — shareable to non-members"
        >
          <Copy className="w-3 h-3" /> Spectator URL
        </button>
      </div>
    </div>
  );
}

